import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  IndianRupee,
  RefreshCw,
  ReceiptText,
  Upload,
  Image as ImageIcon,
  X,
  Building2,
  ChevronDown,
  CalendarClock,
  Eye,
  UserRound,
} from 'lucide-react';
import { driverWalletService } from '../../../services/driverWallet.service';
import { hasPermission } from '../../AdminAuth/components/authStore';
import { appPrompt } from "../../../utils/appPopup";

const PINK = '#d86a78';
const BORDER = '#f2c6cc';


function StatusDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const options = [
    { value: 'REQUESTED', label: 'Requested' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'PAID', label: 'Paid' },
    { value: 'REJECTED', label: 'Rejected' },
    { value: '', label: 'All' },
  ];

  const selected =
    options.find((option) => String(option.value) === String(value)) ||
    options[0];

  useEffect(() => {
    const handleOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  return (
    <div ref={wrapperRef} className="relative w-full md:w-[200px]">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex h-10 w-full items-center justify-between rounded-[12px] border bg-white px-4 text-left text-[13px] font-medium outline-none transition ${
          open
            ? 'border-[#d86a78] ring-2 ring-[#f7d7db]'
            : 'border-[#f2c6cc] text-slate-600 hover:border-[#d86a78]'
        }`}
      >
        <span className="truncate">{selected.label}</span>
        <ChevronDown
          size={16}
          className={`ml-3 shrink-0 text-slate-400 transition ${
            open ? 'rotate-180 text-[#d86a78]' : ''
          }`}
        />
      </button>

      {open ? (
        <div className="absolute left-0 right-0 top-[46px] z-[200] overflow-hidden rounded-[14px] border border-[#f2c6cc] bg-white p-1 shadow-[0_18px_45px_rgba(15,23,42,0.16)]">
          {options.map((option) => {
            const active = String(option.value) === String(value);

            return (
              <button
                key={option.value || 'all'}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={`block w-full rounded-[10px] px-3 py-2.5 text-left text-[13px] transition ${
                  active
                    ? 'bg-[#fff1f3] font-semibold text-[#d86a78]'
                    : 'text-slate-700 hover:bg-[#fff7f8] hover:text-[#d86a78]'
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function money(value) {
  return `₹${Number(value || 0).toFixed(2)}`;
}

function Badge({ children, tone = 'default' }) {
  const styles = {
    REQUESTED: 'bg-amber-50 text-amber-700 border-amber-200',
    APPROVED: 'bg-blue-50 text-blue-700 border-blue-200',
    PAID: 'bg-green-50 text-green-700 border-green-200',
    REJECTED: 'bg-red-50 text-red-700 border-red-200',
    default: 'bg-slate-50 text-slate-600 border-slate-200',
  };

  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-bold tracking-[0.04em] ${styles[tone] || styles.default}`}>
      {children}
    </span>
  );
}

function bankValue(row, key, fallbackKey) {
  return row.bank_snapshot?.[key] || row.bank_snapshot?.[fallbackKey] || '-';
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function DetailField({ label, children, wide = false }) {
  return (
    <div className={wide ? 'sm:col-span-2' : ''}>
      <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">
        {label}
      </div>
      <div className="mt-1 break-words text-sm font-semibold text-slate-700">
        {children || '-'}
      </div>
    </div>
  );
}

function WithdrawalDetailsModal({
  row,
  onClose,
  onPay,
  onReject,
  canManage,
}) {
  if (!row) return null;

  const requestedAt = row.requested_at
    ? new Date(row.requested_at).toLocaleString()
    : '-';

  return (
    <div
      className="fixed inset-0 z-[105] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-[26px] border border-[#f2c6cc] bg-white shadow-[0_28px_80px_rgba(15,23,42,0.22)]">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-[#f5d9de] bg-white/95 px-5 py-4 backdrop-blur-xl sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-[14px] bg-[#fff1f3] text-[#d86a78]">
              <UserRound size={20} />
            </span>
            <div className="min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#d86a78]">
                Withdrawal request
              </div>
              <h2 className="mt-0.5 truncate text-xl font-black text-slate-900">
                {bankValue(row, 'accountHolderName', 'bankHolderName')}
              </h2>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-[#f2c6cc] text-slate-500 transition hover:bg-[#fff5f7] hover:text-[#d86a78]"
            aria-label="Close withdrawal details"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4 p-5 sm:p-6">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-[18px] border border-[#f2c6cc] bg-[#fff8f9] p-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#d86a78]">Requested</div>
              <div className="mt-2 text-xl font-black text-[#d86a78]">{money(row.amount)}</div>
            </div>
            <div className="rounded-[18px] border border-emerald-100 bg-emerald-50/70 p-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-emerald-600">Payable</div>
              <div className="mt-2 text-xl font-black text-emerald-700">{money(row.manual_payable_amount || row.amount)}</div>
            </div>
            <div className="rounded-[18px] border border-slate-200 bg-slate-50 p-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">Status</div>
              <div className="mt-2"><Badge tone={row.status}>{row.status || '-'}</Badge></div>
            </div>
          </div>

          <div className="rounded-[20px] border border-[#f2d9de] p-4 sm:p-5">
            <div className="mb-4 flex items-center gap-2 text-sm font-black text-slate-900">
              <Building2 size={18} className="text-[#d86a78]" /> Bank account details
            </div>
            <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
              <DetailField label="Bank name">{bankValue(row, 'bankName')}</DetailField>
              <DetailField label="Account holder">{bankValue(row, 'accountHolderName', 'bankHolderName')}</DetailField>
              <DetailField label="Account number">{bankValue(row, 'accountNumber', 'bankAccount')}</DetailField>
              <DetailField label="IFSC code">{bankValue(row, 'ifscCode', 'bankIfsc')}</DetailField>
              {row.bank_snapshot?.upiId ? (
                <DetailField label="UPI ID" wide>{row.bank_snapshot.upiId}</DetailField>
              ) : null}
            </div>
          </div>

          <div className="rounded-[20px] border border-slate-200 p-4 sm:p-5">
            <div className="mb-4 flex items-center gap-2 text-sm font-black text-slate-900">
              <CalendarClock size={18} className="text-[#d86a78]" /> Request information
            </div>
            <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
              <DetailField label="Partner ID">{row.partner_id}</DetailField>
              <DetailField label="Requested at">{requestedAt}</DetailField>
              <DetailField label="Request ID" wide>{row.id}</DetailField>
              {row.review_note ? (
                <DetailField label={row.status === 'REJECTED' ? 'Reject reason' : 'Review note'} wide>
                  {row.review_note}
                </DetailField>
              ) : null}
            </div>
          </div>

          <div className="rounded-[20px] border border-slate-200 p-4 sm:p-5">
            <div className="mb-4 flex items-center gap-2 text-sm font-black text-slate-900">
              <ReceiptText size={18} className="text-[#d86a78]" /> Payment proof
            </div>
            {row.manual_payment_screenshot_url ? (
              <div className="grid gap-4 sm:grid-cols-[150px_1fr]">
                <a
                  href={row.manual_payment_screenshot_url}
                  target="_blank"
                  rel="noreferrer"
                  className="block h-[110px] overflow-hidden rounded-[14px] border border-[#f2c6cc] bg-slate-50"
                >
                  <img src={row.manual_payment_screenshot_url} alt="Payment proof" className="h-full w-full object-cover" />
                </a>
                <div className="grid gap-3 sm:grid-cols-2">
                  <DetailField label="Payment reference">{row.manual_payment_reference}</DetailField>
                  <DetailField label="Proof image">
                    <a className="text-[#d86a78] hover:underline" href={row.manual_payment_screenshot_url} target="_blank" rel="noreferrer">Open full image</a>
                  </DetailField>
                  <DetailField label="Admin note" wide>{row.manual_payment_note}</DetailField>
                </div>
              </div>
            ) : (
              <div className="rounded-[14px] bg-slate-50 px-4 py-5 text-center text-sm font-semibold text-slate-400">
                No payment proof has been added yet.
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 flex flex-wrap justify-end gap-2 border-t border-[#f5d9de] bg-white/95 px-5 py-4 backdrop-blur-xl sm:px-6">
          <button type="button" onClick={onClose} className="h-10 rounded-[12px] border border-slate-200 px-4 text-sm font-bold text-slate-600 hover:bg-slate-50">
            Close
          </button>
          {row.status === 'REQUESTED' ? (
            <button
              type="button"
              onClick={() => onReject(row)}
              className={`h-10 rounded-[12px] px-4 text-sm font-bold text-white ${canManage ? 'bg-red-600 hover:bg-red-700' : 'bg-slate-400'}`}
            >
              Reject
            </button>
          ) : null}
          {['REQUESTED', 'APPROVED'].includes(row.status) ? (
            <button
              type="button"
              onClick={() => onPay(row)}
              className={`inline-flex h-10 items-center gap-2 rounded-[12px] px-4 text-sm font-bold text-white ${canManage ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400'}`}
            >
              <ReceiptText size={15} /> {row.status === 'APPROVED' ? 'Mark Paid' : 'Pay / Upload Proof'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ProofModal({ row, onClose, onSubmit, submitting }) {
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('Paid manually by admin');
  const [screenshotUrl, setScreenshotUrl] = useState('');
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Only image file is allowed');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('Image size must be under 2 MB');
      return;
    }

    const dataUrl = await fileToDataUrl(file);
    setScreenshotUrl(dataUrl);
    setPreview(dataUrl);
    setError('');
  };

  const submit = async (event) => {
    event.preventDefault();

    if (!reference.trim()) {
      setError('Payment reference / UTR is required');
      return;
    }

    if (!screenshotUrl.trim()) {
      setError('Payment screenshot image is required');
      return;
    }

    await onSubmit(row.id, {
      reference: reference.trim(),
      screenshotUrl: screenshotUrl.trim(),
      note: note.trim(),
    });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl overflow-hidden rounded-[24px] bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b p-5" style={{ borderColor: BORDER }}>
          <div>
            <h2 className="text-xl font-black text-slate-900">Add Manual Payment Proof</h2>
            <p className="mt-1 text-sm text-slate-500">
              This proof will be saved with the withdrawal and visible in driver withdrawal history.
            </p>
          </div>

          <button type="button" onClick={onClose} className="rounded-full border p-2" style={{ borderColor: BORDER }}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4 p-5">
          <div className="grid gap-3 rounded-2xl border bg-[#fff8f9] p-4 text-sm md:grid-cols-2" style={{ borderColor: BORDER }}>
            <div>
              <div className="text-xs font-bold uppercase text-slate-400">Driver / Holder</div>
              <div className="mt-1 font-black text-slate-800">{bankValue(row, 'accountHolderName', 'bankHolderName')}</div>
            </div>

            <div>
              <div className="text-xs font-bold uppercase text-slate-400">Amount</div>
              <div className="mt-1 font-black text-slate-800">{money(row.manual_payable_amount || row.amount)}</div>
            </div>

            <div>
              <div className="text-xs font-bold uppercase text-slate-400">Bank</div>
              <div className="mt-1 font-bold text-slate-700">{bankValue(row, 'bankName')}</div>
            </div>

            <div>
              <div className="text-xs font-bold uppercase text-slate-400">Account / IFSC</div>
              <div className="mt-1 font-bold text-slate-700">
                {bankValue(row, 'accountNumber', 'bankAccount')} / {bankValue(row, 'ifscCode', 'bankIfsc')}
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm font-bold text-slate-700">Payment Reference / UTR *</label>
            <input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className="mt-2 w-full rounded-xl border px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-pink-100"
              style={{ borderColor: BORDER }}
              placeholder="Example: UTR123456789"
            />
          </div>

          <div>
            <label className="text-sm font-bold text-slate-700">Payment Screenshot Image *</label>

            <div className="mt-2 grid gap-3 md:grid-cols-[1fr_180px]">
              <div className="rounded-xl border border-dashed p-4" style={{ borderColor: BORDER }}>
                <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl bg-[#fff8f9] px-4 py-6 text-center">
                  <Upload size={24} color={PINK} />
                  <span className="text-sm font-bold text-slate-800">Upload payment screenshot</span>
                  <span className="text-xs text-slate-500">PNG/JPG/WebP, max 2 MB</span>
                  <input type="file" accept="image/*" onChange={handleFile} className="hidden" />
                </label>

                <input
                  value={screenshotUrl.startsWith('data:image/') ? '' : screenshotUrl}
                  onChange={(e) => {
                    setScreenshotUrl(e.target.value);
                    setPreview(e.target.value);
                  }}
                  className="mt-3 w-full rounded-xl border px-3 py-2 text-xs outline-none"
                  style={{ borderColor: BORDER }}
                  placeholder="Or paste image URL"
                />
              </div>

              <div className="flex min-h-[150px] items-center justify-center overflow-hidden rounded-xl border bg-slate-50" style={{ borderColor: BORDER }}>
                {preview || screenshotUrl ? (
                  <img
                    src={preview || screenshotUrl}
                    alt="Payment proof preview"
                    className="h-full max-h-[170px] w-full object-cover"
                  />
                ) : (
                  <div className="text-center text-slate-400">
                    <ImageIcon className="mx-auto" />
                    <div className="mt-2 text-xs font-bold">Preview</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm font-bold text-slate-700">Admin Note</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="mt-2 w-full rounded-xl border px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-pink-100"
              style={{ borderColor: BORDER }}
              placeholder="Example: Paid via PhonePe / bank transfer"
            />
          </div>

          {error ? <div className="rounded-xl bg-red-50 px-4 py-3 text-sm font-bold text-red-600">{error}</div> : null}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-xl border px-5 py-3 text-sm font-bold" style={{ borderColor: BORDER }}>
              Cancel
            </button>

            <button disabled={submitting} type="submit" className="rounded-xl bg-green-600 px-5 py-3 text-sm font-bold text-white disabled:opacity-60">
              {submitting ? 'Saving...' : 'Mark Paid & Save Proof'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function DriverWalletPage() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('REQUESTED');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [payingRow, setPayingRow] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [submittingPayment, setSubmittingPayment] = useState(false);

  const canManageDriverWallet = hasPermission('admin.driver_wallet.manage');
  const noManageMessage =
    'You do not have permission to manage Driver Wallet. Please ask admin to assign Manage Driver Wallet Requests permission.';

  const showNoManagePermission = () => {
    window.alert(noManageMessage);
    setMessage(noManageMessage);
  };

  const load = async () => {
    setLoading(true);
    setMessage('');

    try {
      const res = await driverWalletService.listWithdrawRequests({
        status,
        limit: 50,
        offset: 0,
      });

      setItems(Array.isArray(res.items) ? res.items : []);
    } catch (err) {
      setMessage(err.message || 'Failed to load wallet requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [status]);

  const totals = useMemo(() => {
    return items.reduce(
      (acc, row) => {
        acc.amount += Number(row.amount || 0);
        acc.payable += Number(row.manual_payable_amount || row.amount || 0);
        return acc;
      },
      { amount: 0, payable: 0 }
    );
  }, [items]);

  const openPayModal = (row) => {
    if (!canManageDriverWallet) {
      showNoManagePermission();
      return;
    }

    setPayingRow(row);
  };

  const markPaid = async (id, payload) => {
    if (!canManageDriverWallet) {
      showNoManagePermission();
      return;
    }

    setSubmittingPayment(true);
    setMessage('');

    try {
      await driverWalletService.markWithdrawalPaid(id, payload);
      setPayingRow(null);
      await load();
    } catch (err) {
      if (err.status === 403) {
        showNoManagePermission();
      } else {
        setMessage(err.message || 'Failed to mark withdrawal paid');
      }
    } finally {
      setSubmittingPayment(false);
    }
  };

  const reject = async (id) => {
    if (!canManageDriverWallet) {
      showNoManagePermission();
      return;
    }

    setMessage('');

    const reason = await appPrompt('Enter reject reason. This reason will be visible to driver.', { confirmText: 'REJECT', cancelText: 'CANCEL', placeholder: 'Write reject reason...' });

    if (!reason || !reason.trim()) {
      return;
    }

    try {
      setLoading(true);

      await driverWalletService.rejectWithdrawal(id, {
        note: reason.trim(),
      });

      await load();
    } catch (err) {
      if (err.status === 403) {
        showNoManagePermission();
      } else {
        setMessage(err.message || 'Failed to reject withdrawal');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-[22px] font-extrabold tracking-[0.035em] text-[#d86a78]">
            DRIVER WALLET
          </h1>
        </div>

        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-[14px] px-4 text-[13px] font-semibold text-white shadow-[0_7px_18px_rgba(216,106,120,0.16)] transition sm:w-auto sm:px-5 disabled:cursor-not-allowed disabled:opacity-60"
          style={{ backgroundColor: PINK }}
          onMouseEnter={(e) => {
            if (!loading) e.currentTarget.style.backgroundColor = '#c95b69';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = PINK;
          }}
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div
          className="rounded-[20px] px-5 py-4 shadow-[0_8px_24px_rgba(15,23,42,0.025)]"
          style={{ background: '#fff8f9', border: '1px solid #f2c6cc' }}
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#d86a78]">
            Requests
          </div>
          <div className="mt-2 text-[30px] font-extrabold leading-none text-[#d86a78]">
            {items.length}
          </div>
        </div>

        <div
          className="rounded-[20px] px-5 py-4 shadow-[0_8px_24px_rgba(15,23,42,0.025)]"
          style={{ background: '#fffaf0', border: '1px solid #f1dfb8' }}
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#c58b13]">
            Locked Amount
          </div>
          <div className="mt-2 text-[30px] font-extrabold leading-none text-[#b7791f]">
            {money(totals.amount)}
          </div>
        </div>

        <div
          className="rounded-[20px] px-5 py-4 shadow-[0_8px_24px_rgba(15,23,42,0.025)]"
          style={{ background: '#f6fff7', border: '1px solid #cfe7d1' }}
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#69a65f]">
            Manual Payable
          </div>
          <div className="mt-2 text-[30px] font-extrabold leading-none text-[#5b9a50]">
            {money(totals.payable)}
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-[22px] border bg-white shadow-[0_12px_35px_rgba(15,23,42,0.035)]" style={{ borderColor: BORDER }}>
        <div className="flex flex-col gap-3 border-b p-4 md:flex-row md:items-center md:justify-between" style={{ borderColor: BORDER }}>
          <div className="flex items-center gap-2 text-[14px] font-bold text-slate-900">
            <span className="grid h-8 w-8 place-items-center rounded-[10px] bg-[#fff1f3] text-[#d86a78]"><IndianRupee size={16} /></span>
            Withdrawal Requests
          </div>

          <StatusDropdown value={status} onChange={setStatus} />
        </div>

        {!canManageDriverWallet ? (
          <div className="mx-4 mt-4 rounded-xl border bg-amber-50 px-4 py-3 text-sm font-bold text-amber-700" style={{ borderColor: '#fde68a' }}>
            View-only access: you can see wallet requests, but Pay, Reject and Mark Paid need Manage Driver Wallet Requests permission.
          </div>
        ) : null}

        {message ? <div className="p-4 text-sm font-semibold text-red-600">{message}</div> : null}

        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] text-left text-[13px]">
            <thead className="bg-[#fff8f9] text-[10px] uppercase tracking-[0.08em] text-slate-500">
              <tr>
                <th className="px-4 py-3">Driver</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Bank</th>
                <th className="px-4 py-3">Proof</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Requested</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-slate-400">
                    Loading...
                  </td>
                </tr>
              ) : null}

              {!loading && items.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-slate-400">
                    No withdrawal requests found.
                  </td>
                </tr>
              ) : null}

              {items.map((row) => (
                <tr key={row.id} className="border-t align-middle transition hover:bg-[#fffafb]" style={{ borderColor: BORDER }}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-800">
                      {bankValue(row, 'accountHolderName', 'bankHolderName')}
                    </div>
                    <div className="mt-0.5 max-w-[160px] truncate text-[11px] text-slate-400">{row.partner_id}</div>
                  </td>

                  <td className="whitespace-nowrap px-4 py-3">
                    <div className="font-bold text-slate-800">{money(row.amount)}</div>
                    <div className="mt-0.5 text-[11px] text-slate-400">
                      Payable {money(row.manual_payable_amount || row.amount)}
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-[#fff1f3] text-[#d86a78]">
                        <Building2 size={14} />
                      </span>
                      <div className="min-w-0">
                        <div className="max-w-[150px] truncate font-semibold text-slate-700">{bankValue(row, 'bankName')}</div>
                        <div className="mt-0.5 max-w-[150px] truncate text-[11px] text-slate-400">
                          A/C {bankValue(row, 'accountNumber', 'bankAccount')}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    {row.manual_payment_screenshot_url ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700">
                        <ImageIcon size={13} /> Added
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">Not added</span>
                    )}
                  </td>

                  <td className="px-4 py-3">
                    <Badge tone={row.status}>{row.status}</Badge>
                  </td>

                  <td className="whitespace-nowrap px-4 py-3">
                    <div className="font-medium text-slate-700">
                      {row.requested_at ? new Date(row.requested_at).toLocaleDateString() : '-'}
                    </div>
                    <div className="mt-0.5 text-[11px] text-slate-400">
                      {row.requested_at ? new Date(row.requested_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                    </div>
                  </td>

                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setDetailRow(row)}
                        className="inline-flex h-8 items-center gap-1.5 rounded-[9px] border border-[#f2c6cc] bg-white px-2.5 text-[11px] font-semibold text-[#d86a78] transition hover:bg-[#fff3f5]"
                      >
                        <Eye size={14} /> Details
                      </button>

                    {row.status === 'REQUESTED' ? (
                        <button
                          type="button"
                          onClick={() => openPayModal(row)}
                          className={`inline-flex h-8 items-center gap-1.5 rounded-[9px] px-2.5 text-[11px] font-semibold text-white ${canManageDriverWallet ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400'}`}
                        >
                          <ReceiptText size={14} /> Pay
                        </button>
                    ) : null}

                    {row.status === 'APPROVED' ? (
                      <button
                        type="button"
                        onClick={() => openPayModal(row)}
                        className={`inline-flex h-8 items-center gap-1.5 rounded-[9px] px-2.5 text-[11px] font-semibold text-white ${canManageDriverWallet ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400'}`}
                      >
                        <ReceiptText size={14} /> Mark Paid
                      </button>
                    ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {detailRow ? (
        <WithdrawalDetailsModal
          row={detailRow}
          onClose={() => setDetailRow(null)}
          canManage={canManageDriverWallet}
          onPay={(row) => {
            setDetailRow(null);
            openPayModal(row);
          }}
          onReject={(row) => {
            setDetailRow(null);
            reject(row.id);
          }}
        />
      ) : null}

      {payingRow ? (
        <ProofModal
          row={payingRow}
          onClose={() => setPayingRow(null)}
          onSubmit={markPaid}
          submitting={submittingPayment}
        />
      ) : null}
    </div>
  );
}
